import { useState, useEffect } from "react";
import * as Scrivito from "scrivito";

const OAI_BASE = "https://opus-hshn.bsz-bw.de/oai";
const SEARCH_BASE = "https://opus-hshn.bsz-bw.de/solrsearch/index/search";

function parsePublication(xmlText) {
  const parser = new window.DOMParser();
  const doc = parser.parseFromString(xmlText, "application/xml");

  const getText = (tag) => {
    const el = doc.querySelector(tag);
    return el ? el.textContent.trim() : null;
  };

  const getAll = (tag) =>
    Array.from(doc.querySelectorAll(tag)).map((el) => el.textContent.trim());

  const identifiers = getAll("identifier");
  const pdfUrl = identifiers.find((id) => id.endsWith(".pdf")) || null;
  const frontdoorUrl =
    identifiers.find((id) => id.includes("opus-hshn.bsz-bw.de/frontdoor")) ||
    null;

  const types = getAll("type");
  const docType =
    types.find((t) => t.startsWith("doc-type:"))?.replace("doc-type:", "") ||
    types[0] ||
    null;

  return {
    title: getText("title"),
    authors: getAll("creator"),
    year: getText("date"),
    abstract: getText("description"),
    subject: getAll("subject"),
    language: getText("language"),
    publisher: getText("publisher"),
    docType,
    pdfUrl,
    frontdoorUrl,
  };
}

function parseSearchResults(html) {
  const parser = new window.DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const results = [];
  const seen = new Set();

  doc
    .querySelectorAll('a[href*="/frontdoor/index/index/docId/"]')
    .forEach((link) => {
      const href = link.getAttribute("href");
      const match = href.match(/\/frontdoor\/index\/index\/docId\/(\d+)/);
      if (!match || seen.has(match[1])) return;
      seen.add(match[1]);

      const title = link.textContent.trim();
      if (!title) return;

      const docId = match[1];
      const container =
        link.closest(".result_box") || link.closest("li") || link.parentElement;

      let authors = null;
      let year = null;
      let docType = null;

      if (container) {
        const creatorEl = container.querySelector(
          ".result_creator, .author, [class*='creator'], [class*='author']"
        );
        if (creatorEl) authors = creatorEl.textContent.trim();

        const dateEl = container.querySelector(
          ".result_date, .date, [class*='date']"
        );
        if (dateEl) year = dateEl.textContent.trim();

        const typeEl = container.querySelector(
          ".result_publication_type, .doctype, [class*='type']"
        );
        if (typeEl) docType = typeEl.textContent.trim();
      }

      results.push({
        docId,
        title,
        authors,
        year,
        docType,
        frontdoorUrl: `https://opus-hshn.bsz-bw.de/frontdoor/index/index/docId/${docId}`,
      });
    });

  return results;
}

const DOC_TYPE_LABELS = {
  bachelorThesis: "Bachelor Thesis",
  masterThesis: "Master Thesis",
  doctoralThesis: "Doctoral Thesis",
  article: "Article",
  bookPart: "Book Chapter",
  book: "Book",
  report: "Report",
  conferenceObject: "Conference Paper",
};

function formatDocType(raw) {
  return DOC_TYPE_LABELS[raw] || raw || "Publication";
}

function formatLanguage(lang) {
  if (!lang) return null;
  const map = { deu: "Deutsch", eng: "English", fra: "Français" };
  return map[lang] || lang;
}

function PublicationCard({ pub }) {
  const [showAbstract, setShowAbstract] = useState(false);

  return (
    <>
      <div className="opus-publication-widget__header">
        <span className="opus-publication-widget__type">
          {formatDocType(pub.docType)}
        </span>
        {pub.year && (
          <span className="opus-publication-widget__year">{pub.year}</span>
        )}
      </div>

      <h3 className="opus-publication-widget__title">{pub.title}</h3>

      {pub.authors.length > 0 && (
        <p className="opus-publication-widget__authors">
          {pub.authors.join("; ")}
        </p>
      )}

      {pub.subject.length > 0 && (
        <div className="opus-publication-widget__subjects">
          {pub.subject.map((s) => (
            <span key={s} className="badge bg-blue rounded-pill">
              {s}
            </span>
          ))}
        </div>
      )}

      {pub.abstract && (
        <div className="opus-publication-widget__abstract-wrapper">
          <button
            type="button"
            className="opus-publication-widget__abstract-toggle"
            onClick={() => setShowAbstract((v) => !v)}
          >
            <i
              className={`las ${
                showAbstract ? "la-angle-up" : "la-angle-down"
              } me-1`}
            />
            {showAbstract ? "Hide Abstract" : "Show Abstract"}
          </button>
          {showAbstract && (
            <p className="opus-publication-widget__abstract">{pub.abstract}</p>
          )}
        </div>
      )}

      <div className="opus-publication-widget__links">
        {pub.frontdoorUrl && (
          <a
            href={pub.frontdoorUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="btn bg-blue"
          >
            <i className="las la-external-link-alt me-1" />
            OPUS Entry
          </a>
        )}
        {pub.pdfUrl && (
          <a
            href={pub.pdfUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="btn bg-white"
          >
            <i className="las la-file-pdf me-1" />
            Download as PDF
          </a>
        )}
      </div>

      {pub.language && (
        <strong className="opus-publication-widget__meta">
          Language: {formatLanguage(pub.language)}
        </strong>
      )}
    </>
  );
}

function PublicationListItem({ pub }) {
  return (
    <li className="opus-publication-widget__list-item">
      <div className="opus-publication-widget__list-item-meta">
        {pub.docType && (
          <span className="opus-publication-widget__type">
            {formatDocType(pub.docType)}
          </span>
        )}
        {pub.year && (
          <span className="opus-publication-widget__year">{pub.year}</span>
        )}
      </div>
      <a
        href={pub.frontdoorUrl}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="opus-publication-widget__list-item-title"
      >
        {pub.title}
      </a>
      {pub.authors && (
        <p className="opus-publication-widget__authors">{pub.authors}</p>
      )}
    </li>
  );
}

Scrivito.provideComponent(
  "OpusPublicationWidget",
  Scrivito.connect(({ widget }) => {
    const mode = widget.get("mode") || "id";
    const opusId = widget.get("opusId");
    const authorName = widget.get("authorName");

    const [pub, setPub] = useState(null);
    const [pubList, setPubList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
      if (mode !== "id") return;
      if (!opusId || !opusId.trim()) {
        setPub(null);
        setError(null);
        return;
      }

      let cancelled = false;
      setLoading(true);
      setError(null);
      setPub(null);

      const identifier = `oai:opus-hshn:${opusId.trim()}`;
      const url = `${OAI_BASE}?verb=GetRecord&metadataPrefix=oai_dc&identifier=${encodeURIComponent(
        identifier
      )}`;

      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.text();
        })
        .then((xml) => {
          if (cancelled) return;
          const parsed = parsePublication(xml);
          if (!parsed.title) {
            setError(`No record found for OPUS ID ${opusId}.`);
          } else {
            setPub(parsed);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          setError(`Failed to load publication: ${err.message}`);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });

      return () => {
        cancelled = true;
      };
    }, [mode, opusId]);

    useEffect(() => {
      if (mode !== "author") return;
      if (!authorName || !authorName.trim()) {
        setPubList([]);
        setError(null);
        return;
      }

      let cancelled = false;
      setLoading(true);
      setError(null);
      setPubList([]);

      const url = `${SEARCH_BASE}/searchtype/authorsearch/author/${encodeURIComponent(
        authorName.trim()
      )}`;

      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.text();
        })
        .then((html) => {
          if (cancelled) return;
          const results = parseSearchResults(html);
          if (results.length === 0) {
            setError(`No publications found for "${authorName}".`);
          } else {
            setPubList(results);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          setError(`Failed to load publications: ${err.message}`);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });

      return () => {
        cancelled = true;
      };
    }, [mode, authorName]);

    if (mode === "id") {
      if (!opusId || !opusId.trim()) {
        return (
          <Scrivito.WidgetTag className="opus-publication-widget --empty">
            <div className="alert alert-info">
              <i className="las la-book me-2" />
              Please enter an OPUS ID in the widget settings.
            </div>
          </Scrivito.WidgetTag>
        );
      }
    }

    if (mode === "author") {
      if (!authorName || !authorName.trim()) {
        return (
          <Scrivito.WidgetTag className="opus-publication-widget --empty">
            <div className="alert alert-info">
              <i className="las la-user me-2" />
              Please enter an author name in the widget settings.
            </div>
          </Scrivito.WidgetTag>
        );
      }
    }

    if (loading) {
      return (
        <Scrivito.WidgetTag className="opus-publication-widget --loading">
          <div className="opus-publication-widget__loader">
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
            />
            Loading publication{mode === "author" ? "s" : ""}…
          </div>
        </Scrivito.WidgetTag>
      );
    }

    if (error) {
      return (
        <Scrivito.WidgetTag className="opus-publication-widget --error">
          <div className="alert alert-warning">
            <i className="las la-exclamation-triangle me-2" />
            {error}
          </div>
        </Scrivito.WidgetTag>
      );
    }

    if (mode === "author") {
      if (pubList.length === 0) return null;
      return (
        <Scrivito.WidgetTag>
          <div className="opus-publication-widget__list">
            <ul className="opus-publication-widget__list-items">
              {pubList.map((pubItem) => (
                <PublicationListItem key={pubItem.docId} pub={pubItem} />
              ))}
            </ul>
          </div>
        </Scrivito.WidgetTag>
      );
    }

    if (!pub) return null;

    return (
      <Scrivito.WidgetTag className="opus-publication-widget mb-5">
        <PublicationCard pub={pub} />
      </Scrivito.WidgetTag>
    );
  })
);
